import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Bar,
  Cell,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { colors2, UI_CAPTION_SEMIBOLD } from "oolib";
import styled from "styled-components";

const ChartWrapper = styled.div`
  position: relative;
  *:focus {
    outline: none !important;
  }
`;

const LabelOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const COUNT_GAP = 16;
const FADE_WIDTH = 30;

const Row = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0 ${({ $hPad }) => $hPad}px;
  gap: ${COUNT_GAP}px;
  border: 1px solid transparent;
  border-radius: 5px;
  box-sizing: border-box;
  pointer-events: ${({ $clickable }) => ($clickable ? "auto" : "none")};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  ${({ $isActive, $isHoverOnly }) =>
    $isActive &&
    `
    border-color: ${$isHoverOnly ? colors2.grey30 : colors2.grey80};
    ${!$isHoverOnly ? "box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.15);" : ""}
  `}
`;

const NameWrapper = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const NameText = styled.span`
  display: inline-block;
  white-space: nowrap;
  max-width: 100%;
  ${({ $isOverflowing }) =>
    $isOverflowing &&
    `
      mask-image: linear-gradient(to right, black calc(100% - ${FADE_WIDTH}px), transparent 100%);
      -webkit-mask-image: linear-gradient(to right, black calc(100% - ${FADE_WIDTH}px), transparent 100%);
    `}
`;

// Matches _base_barchart_ui_configs values from the shared BarChart
const LABEL_TO_BAR_GAP = -16;
const LABEL_HEIGHT = 16.5667;
const LABEL_TO_NEXT_BAR_GAP = 12;
const LABEL_LEFT_SPACING = 8;
const GAP_BETWEEN_BAR_SECTIONS = -10;
const BAR_RADIUS = 4;
const MIN_BAR_WIDTH = 2;
const HOVER_RECT_STROKE_WIDTH = 1;

const CHART_OFFSET_TOP =
  LABEL_TO_BAR_GAP +
  LABEL_HEIGHT +
  (LABEL_TO_NEXT_BAR_GAP / 2 - 6);

const calcSectionHeight = (barHeight) =>
  barHeight + LABEL_TO_BAR_GAP + LABEL_HEIGHT;

const calcYPos = (index, sectionHeight, gap) =>
  (sectionHeight + gap) * index;

const calcTotalHeight = (count, sectionHeight, gap) =>
  sectionHeight * count +
  gap * (count - 1) +
  HOVER_RECT_STROKE_WIDTH * 2;

const FadingName = ({ text }) => {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  }, [text]);

  return (
    <NameWrapper ref={ref}>
      <UI_CAPTION_SEMIBOLD color={colors2.grey80}>
        <NameText $isOverflowing={isOverflowing}>{text}</NameText>
      </UI_CAPTION_SEMIBOLD>
    </NameWrapper>
  );
};

export const SimpleBarChart = ({
  data: rawData = [],
  barColor = colors2.blue50,
  barHeight = 26,
  barGap = 2,
  onClick,
  activeName,
}) => {
  const maxCount = useMemo(
    () => Math.max(...rawData.map((d) => d.count), 1),
    [rawData]
  );

  const sectionHeight = calcSectionHeight(barHeight);
  const effectiveGap = LABEL_TO_NEXT_BAR_GAP + GAP_BETWEEN_BAR_SECTIONS + barGap;

  const chartData = useMemo(
    () =>
      rawData.map((item) => ({
        ...item,
        __value: item.count,
      })),
    [rawData]
  );

  const cellProps = useMemo(
    () =>
      chartData.map((_, index) => ({
        y: calcYPos(index, sectionHeight, effectiveGap) + CHART_OFFSET_TOP,
        height: barHeight,
      })),
    [chartData, sectionHeight, effectiveGap, barHeight]
  );

  const [activeIndex, setActiveIndex] = useState(undefined);

  if (rawData.length === 0) return null;

  return (
    <ChartWrapper onMouseLeave={() => setActiveIndex(undefined)}>
      <ResponsiveContainer
        width="100%"
        height={calcTotalHeight(rawData.length, sectionHeight, effectiveGap)}
      >
        <RechartsBarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        >
          <XAxis hide type="number" domain={[0, maxCount]} />
          <YAxis hide type="category" />
          <Bar
            dataKey="__value"
            radius={BAR_RADIUS}
            isAnimationActive={false}
          >
            {cellProps.map((cell, index) => (
              <Cell
                key={index}
                y={cell.y}
                height={cell.height}
                fill={barColor}
                radius={BAR_RADIUS}
                {...(chartData[index].__value === 0
                  ? { width: MIN_BAR_WIDTH }
                  : {})}
              />
            ))}
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
      <LabelOverlay>
        {chartData.map((item, index) => {
          const isHoverActive = activeIndex === index;
          const isLockedActive = activeName && activeName === item.name;
          const isActive = isHoverActive || isLockedActive;
          return (
            <Row
              key={index}
              $hPad={LABEL_LEFT_SPACING}
              $clickable={!!onClick}
              $isActive={isActive}
              $isHoverOnly={isHoverActive && !isLockedActive}
              style={{
                top: cellProps[index].y,
                height: cellProps[index].height,
              }}
              onMouseOver={() => setActiveIndex(index)}
              onMouseOut={() => setActiveIndex(undefined)}
              onClick={onClick ? () => onClick(item) : undefined}
            >
              <FadingName text={item.name} />
              <UI_CAPTION_SEMIBOLD color={colors2.grey80}>
                {item.displayValue ?? item.count}
              </UI_CAPTION_SEMIBOLD>
            </Row>
          );
        })}
      </LabelOverlay>
    </ChartWrapper>
  );
};
